import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/system';
import { useMemo, useState } from 'react';
import {
  Change,
  ChangeType,
  ContactChangeType,
  HistoryEntity,
  RoomChangeType,
  TemplateChangeType,
  ToolChangeType,
} from '../../../modules/dashboard/models/dashboard.types';
import {
  faPaperPlane,
  faPeopleRoof,
  faScrewdriverWrench,
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Tooltip } from '../../../shared/ui/Tooltip/Tooltip';
import styles from './ChangesPage.module.scss';
import { useNewsCenter } from '../../../modules/newscenter/hooks/use-news-center.hook';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Menu } from '../../../shared/ui/Menu/Menu';
import { MessageAction } from '../../../modules/newscenter/models/message.types';
import { MenuItem } from '../../../shared/models/shared.types';
import pg_icon from '../../../assets/pictures/pg_profile.png';

const ChangeContent = ({
  change,
  changeTypeDetail,
}: {
  change: Change;
  changeTypeDetail: any;
}) => {
  const { t } = useTranslation();
  const { removeHtmlTags, translateBoolean } = useNewsCenter();

  const fullName = change.contact?.user_1_name ?? t('app.nameless');
  const getChangeContent = () => {
    switch (changeTypeDetail) {
      /******************/
      /* Contact Change */
      /******************/
      case ContactChangeType.Accepted:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted', {
              user1: fullName ?? '',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLink:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link', {
              user1: fullName ?? '',
              group: change.contact?.user_2_name ?? 'Gruppenname',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLinkAdmin:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link_admin', {
              user1: fullName ?? '',
              community: change.contact?.community?.name ?? 'Comminity',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLinkCommunity:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link_community', {
              user1: fullName ?? '',
              community: change.contact?.community?.name ?? 'Comminity',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLinkEmployee:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link_employee', {
              user1: fullName ?? '',
              community: change.contact?.community?.name ?? 'Comminity',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLinkGroup:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link_group', {
              user1: fullName ?? '',
              community: change.contact?.community?.name ?? 'Comminity',
              group: change.contact?.user_2_name ?? 'Gruppenname',
            })}
          </div>
        );
      case ContactChangeType.AcceptedLinkMember:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.accepted_link_member', {
              user1: fullName ?? '',
              community: change.contact?.community?.name ?? 'Comminity',
            })}
          </div>
        );
      case ContactChangeType.CommunityAccepted:
        return (
          <div className={styles['change-item-text']}>
            {t('dashboard.widgets.changes.contact.community_accepted', {
              user1: change.contact?.user_1_name ?? `(${t('app.nameless')})`,
              user2: change.contact?.user_2_name ?? `(${t('app.nameless')})`,
            })}
          </div>
        );
      case ContactChangeType.CommunityInvited:
        return (
          <div className={styles['change-item-text']}>
            {!change.contact?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.community_invited', {
              user1: change.contact?.user_1_name ?? '',
              community: change.contact?.user_2_name ?? t('app.nameless'),
            })}
          </div>
        );
      case ContactChangeType.CommunityRejected:
        break;
      case ContactChangeType.Rejected:
        return (
          <div className={styles['change-item-text']}>
            {!fullName && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.contact.rejected', {
              user1: fullName ?? '',
            })}
          </div>
        );
      /***************/
      /* Room Change */
      /***************/
      case RoomChangeType.Close:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.close', {
              user1: change.room?.user_1_name ?? '',
              groupName: change.room?.message_room_name,
            })}
          </div>
        );
      case RoomChangeType.InviteMe:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.invite', {
              user1: change.room?.user_1_name ?? '',
              groupName: change.room?.message_room_name,
            })}
          </div>
        );
      case RoomChangeType.InviteOthers:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.invite_others', {
              user1: change.room?.user_1_name ?? '',
              user2: change.room?.user_2_name
                ? JSON.parse(change.room?.user_2_name).join(', ').toString()
                : undefined,
              groupName: change.room?.message_room_name,
            })}
          </div>
        );
      case RoomChangeType.Leave:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.leave', {
              user1: change.room?.user_1_name ?? '',
            })}
          </div>
        );
      case RoomChangeType.Mention:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.mention', {
              user1: change.room?.user_1_name ?? '',
            })}
          </div>
        );
      case RoomChangeType.NewMessage:
        return;
      case RoomChangeType.Removed:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.remove', {
              user1: change.room?.user_1_name ?? '',
              user2: change.room?.user_2_name ?? t('app.nameless'),
              groupName: change.room?.message_room_name,
            })}
          </div>
        );
      case RoomChangeType.RemovedMe:
        return (
          <div className={styles['change-item-text']}>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.remove_me', {
              user1: change.room?.user_1_name ?? '',
              groupName: change.room?.message_room_name,
            })}
          </div>
        );
      case RoomChangeType.Sticky:
        return (
          <div className={styles['change-item-text']}>
            {t('dashboard.widgets.changes.room.sticky', {
              user1: change.room?.user_1_name ?? `(${t('app.nameless')})`,
            })}
          </div>
        );
      case RoomChangeType.ToolMultiRowNewMessage:

      case RoomChangeType.ToolNewMessage:
        return (
          <div>
            {!change.room?.user_1_name && (
              <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
            )}
            {t('dashboard.widgets.changes.room.new_message', {
              user1: change.room?.user_1_name ?? '',
              toolName: change.room?.tool_name,
            })}
          </div>
        );

      /*******************/
      /* Template Change */
      /*******************/
      case TemplateChangeType.Approved:
        return;
      case TemplateChangeType.Change:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.template.change', {
              userName:
                change.template?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.template?.tool_name,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.template.change', {
                userName:
                  change.template?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.template?.tool_name,
              })}
            </div>
          </Tooltip>
        );
      case TemplateChangeType.Create:
        return;
      case TemplateChangeType.Delete:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.template.delete', {
              userName:
                change.template?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.template?.tool_name,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.template.delete', {
                userName:
                  change.template?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.template?.tool_name,
              })}
            </div>
          </Tooltip>
        );
      case TemplateChangeType.Deployed:
        return;
      case TemplateChangeType.Publish:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.template.publish', {
              userName:
                change.template?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.template?.tool_name,
            })}$
                    $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.template.publish', {
                userName:
                  change.template?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.template?.tool_name,
              })}
            </div>
          </Tooltip>
        );
      case TemplateChangeType.Question:
        return;
      /***************/
      /* Tool Change */
      /***************/
      case ToolChangeType.Add:
        return;
      case ToolChangeType.Change:
        if (change.tool?.count && change.tool?.count > 1) {
          return (
            <Tooltip
              title={`${
                change.tool?.community?.name
                  ? t('dashboard.widgets.changes.tool.change_duplicate', {
                      user1: change.tool?.user_1_name ?? t('app.nameless'),
                      toolName: change.tool?.tool_name ?? '',
                      count: change.tool?.count ?? '',
                      communityName: change.tool?.community?.name ?? '',
                    })
                  : t(
                      'dashboard.widgets.changes.tool.change_duplicate_no_community',
                      {
                        user1: change.tool?.user_1_name ?? t('app.nameless'),
                        toolName: change.tool?.tool_name ?? '',
                        count: change.tool?.count ?? '',
                      }
                    )
              }$$`}
            >
              <div className={styles['change-item-text']}>
                {!change.tool?.user_1_name && (
                  <span style={{ fontStyle: 'italic' }}>
                    {t('app.nameless')}
                  </span>
                )}
                {change.tool?.community?.name
                  ? t('dashboard.widgets.changes.tool.change_duplicate', {
                      user1: change.tool?.user_1_name ?? '',
                      toolName: change.tool?.tool_name ?? '',
                      count: change.tool?.count ?? '',
                      communityName: change.tool?.community?.name ?? '',
                    })
                  : t(
                      'dashboard.widgets.changes.tool.change_duplicate_no_community',
                      {
                        user1: change.tool?.user_1_name ?? '',
                        toolName: change.tool?.tool_name ?? '',
                        count: change.tool?.count ?? '',
                      }
                    )}
              </div>
            </Tooltip>
          );
        }
        return;
      case ToolChangeType.Create:
        return (
          <Tooltip
            title={`${
              change.tool?.community?.name
                ? t('dashboard.widgets.changes.tool.create', {
                    user1: change.tool?.user_1_name ?? t('app.nameless'),
                    toolName: change.tool?.tool_name,
                    communityName: change.tool?.community?.name,
                  })
                : t('dashboard.widgets.changes.tool.create_no_community', {
                    user1: change.tool?.user_1_name ?? t('app.nameless'),
                    toolName: change.tool?.tool_name,
                  })
            }$
          $`}
          >
            <div className={styles['change-item-text']}>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {change.tool?.community?.name
                ? t('dashboard.widgets.changes.tool.create', {
                    user1: change.tool?.user_1_name ?? '',
                    toolName: change.tool?.tool_name,
                    communityName: change.tool?.community?.name,
                  })
                : t('dashboard.widgets.changes.tool.create_no_community', {
                    user1: change.tool?.user_1_name ?? '',
                    toolName: change.tool?.tool_name,
                  })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.Delete:
        return (
          <Tooltip
            title={`${
              change.tool?.community?.name
                ? t('dashboard.widgets.changes.tool.delete', {
                    user1: change.tool?.user_1_name ?? t('app.nameless'),
                    toolName: change.tool?.tool_name,
                    communityName: change.tool?.community?.name,
                  })
                : t('dashboard.widgets.changes.tool.delete_no_community', {
                    user1: change.tool?.user_1_name ?? t('app.nameless'),
                    toolName: change.tool?.tool_name,
                  })
            }$
          $`}
          >
            <div className={styles['change-item-text']}>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {change.tool?.community?.name
                ? t('dashboard.widgets.changes.tool.delete', {
                    user1: change.tool?.user_1_name ?? '',
                    toolName: change.tool?.tool_name,
                    communityName: change.tool?.community?.name,
                  })
                : t('dashboard.widgets.changes.tool.delete_no_community', {
                    user1: change.tool?.user_1_name ?? '',
                    toolName: change.tool?.tool_name,
                  })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.MultiAssigned:
        return (
          <Tooltip>
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.multi_assigned', {
                rowName: change.tool?.changed_row?.new_value,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.MultiRemoved:
        return (
          <Tooltip>
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.multi_removed', {
                rowName: change.tool?.changed_row?.new_value,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.MultiRowAdd:
        if (
          change.tool?.count &&
          change.tool?.count > 1 &&
          change.tool?.community?.name
        ) {
          return (
            <Tooltip>
              <div className={styles['change-item-text']}>
                {!change.tool?.user_1_name && (
                  <span style={{ fontStyle: 'italic' }}>
                    {t('app.nameless')}
                  </span>
                )}
                {t('dashboard.widgets.changes.tool.multi_rows_add', {
                  user1: change.tool?.user_1_name ?? '',
                  toolName: change.tool?.tool_name,
                  tableName: change.tool?.table_name,
                  communityName: change.tool?.community?.name,
                })}
              </div>
            </Tooltip>
          );
        }
        return;
      case ToolChangeType.MultiRowUpdate:
        // if (
        //   change.tool?.changed_row?.old_value &&
        //   change.tool?.changed_row?.old_value !== '' &&
        //   change.tool?.changed_row?.column_name &&
        //   change.tool?.changed_row?.multi_element_name
        // ) {
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.tool.multi_row_update', {
              user1: change.tool?.user_1_name ?? t('app.nameless'),
              toolName: change.tool?.tool_name,
              columnName: change.tool?.changed_row?.column_name,
              multiElementName: change.tool?.changed_row?.multi_element_name,
              oldValue: removeHtmlTags(change.tool?.changed_row?.old_value),
              newValue: removeHtmlTags(change.tool?.changed_row?.new_value),
            })}$
              $`}
          >
            <div className={styles['change-item-text']}>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {t('dashboard.widgets.changes.tool.multi_row_update', {
                user1: change.tool?.user_1_name ?? '',
                toolName: change.tool?.tool_name,
                // communityName: change.tool?.community?.name,
                columnName: change.tool?.changed_row?.column_name,
                multiElementName: change.tool?.changed_row?.multi_element_name,
                oldValue: removeHtmlTags(
                  translateBoolean(change.tool?.changed_row?.old_value ?? '')
                ),
                newValue: removeHtmlTags(
                  translateBoolean(change.tool?.changed_row?.new_value ?? '')
                ),
              })}
            </div>
          </Tooltip>
        );
      // }
      // return;
      case ToolChangeType.MultiSeleted:
        return (
          <Tooltip>
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.multi_seleted', {
                rowName: change.tool?.changed_row?.new_value,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.Publish:
        return;
      case ToolChangeType.Removed:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.tool.removed', {
              user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.tool?.tool_name,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.removed', {
                user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.tool?.tool_name,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.RightAssigned:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.tool.right_assigned', {
              user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.tool?.tool_name,
              right: change.tool?.right?.name,
              role: change.tool?.right?.role,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.right_assigned', {
                user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.tool?.tool_name,
                right: change.tool?.right?.name,
                role: change.tool?.right?.role,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.RightRemoved:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.tool.right_removed', {
              user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.tool?.tool_name,
              right: change.tool?.right?.name,
              role: change.tool?.right?.role,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.right_removed', {
                user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.tool?.tool_name,
                right: change.tool?.right?.name,
                role: change.tool?.right?.role,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.Selected:
        return (
          <Tooltip
            title={`${t('dashboard.widgets.changes.tool.selected', {
              user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
              toolName: change.tool?.tool_name,
            })}$
          $`}
          >
            <div className={styles['change-item-text']}>
              {t('dashboard.widgets.changes.tool.selected', {
                user1: change.tool?.user_1_name ?? `(${t('app.nameless')})`,
                toolName: change.tool?.tool_name,
              })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.Settings:
        return;
      case ToolChangeType.ToolChatGroupAdd:
        return (
          <Tooltip
            title={`${
              change.tool?.tool_name &&
              t('dashboard.widgets.changes.tool.add_tool_chat_group', {
                user1: change.tool?.user_1_name,
                toolName: change.tool?.tool_name,
              })
            }$
          $`}
          >
            <div>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {change.tool?.tool_name &&
                t('dashboard.widgets.changes.tool.add_tool_chat_group', {
                  user1: change.tool?.user_1_name,
                  toolName: change.tool?.tool_name,
                })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.ToolChatGroupRemove:
        return (
          <Tooltip
            title={`${
              change.tool?.tool_name &&
              t('dashboard.widgets.changes.tool.remove_tool_chat_group', {
                user1: change.tool?.user_1_name,
                toolName: change.tool?.tool_name,
              })
            }$
          $`}
          >
            <div>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {change.tool?.tool_name &&
                t('dashboard.widgets.changes.tool.remove_tool_chat_group', {
                  user1: change.tool?.user_1_name,
                  toolName: change.tool?.tool_name,
                })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.ToolChatMultiRowAdd:
        return (
          <Tooltip>
            <div>
              {!change.tool?.user_1_name && (
                <span style={{ fontStyle: 'italic' }}>{t('app.nameless')}</span>
              )}
              {change.tool?.tool_name &&
                t('dashboard.widgets.changes.tool.add_tool_chat_multi_row', {
                  user1: change.tool?.user_1_name,
                  rowName: change.tool?.changed_row?.new_value,
                })}
            </div>
          </Tooltip>
        );
      case ToolChangeType.Update:
        return <div></div>;

      default:
        return <div></div>;
    }
  };

  return <>{getChangeContent()}</>;
};

export default ChangeContent;
